<template>
  <div class="chart-grid chart-grid-3">
    <div class="chart-card">
      <h3 class="card-title">Overall Status</h3>
      <div v-if="!hasStatusData" class="no-data">No Records Found</div>
      <div v-else class="chart-body">
        <apexchart
          type="donut"
          height="300"
          :options="statusChartOptions"
          :series="statusSeries"
        />
      </div>
    </div>
    <div class="chart-card">
      <h3 class="card-title">Status by Team</h3>
      <div v-if="!hasResultTeamData" class="no-data">No Records Found</div>
      <div v-else class="chart-body">
        <apexchart
          type="bar"
          height="300"
          :options="resultTeamChartOptions"
          :series="resultTeamSeries"
        />
      </div>
    </div>
    <div class="chart-card">
      <h3 class="card-title">Status by Location</h3>
      <div v-if="!hasResultLocationData" class="no-data">No Records Found</div>
      <div v-else class="chart-body">
        <apexchart
          type="bar"
          height="300"
          :options="resultLocationChartOptions"
          :series="resultLocationSeries"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { KPI_RESULT_ORDER, KPI_TEAM_RESULT_ORDER, stackedStatusSeries } from "../../utils/dashboardChartData";
import { RESULT_COLORS, donutOptions, stackedBarOptions } from "../../utils/dashboardTheme";

export default defineComponent({
  name: "KpisSection",
  props: {
    kpiResults: { type: Array, default: () => [] },
    kpiResultsByTeam: { type: Array, default: () => [] },
    kpiResultsByLocation: { type: Array, default: () => [] },
  },
  data(): any {
    return {};
  },
  computed: {
    statusCounts(): { label: string; total: number; color: string }[] {
      const totals: Record<string, number> = { Pass: 0, Fail: 0, "No Instances": 0 };
      ((this.kpiResults as any[]) || []).forEach((item: any) => {
        const result = String(item.result || "");
        if (result in totals) {
          totals[result] += 1;
        }
      });
      return [
        { label: "Pass", total: totals.Pass, color: RESULT_COLORS.Pass },
        { label: "Fail", total: totals.Fail, color: RESULT_COLORS.Fail },
        { label: "No Instances", total: totals["No Instances"], color: RESULT_COLORS["No Instances"] },
      ].filter((item) => item.total > 0);
    },
    hasStatusData(): boolean {
      return this.statusCounts.length > 0;
    },
    statusKey(): string {
      const counts: { label: string; total: number; color: string }[] = this.statusCounts;
      return counts.map((item: { label: string; total: number; color: string }) => `${item.label}-${item.total}`).join("-");
    },
    statusSeries(): number[] {
      const counts: { label: string; total: number; color: string }[] = this.statusCounts;
      return counts.map((item: { label: string; total: number; color: string }) => item.total);
    },
    statusChartOptions(): Record<string, any> {
      const counts: { label: string; total: number; color: string }[] = this.statusCounts;
      return donutOptions(
        counts.map((item: { label: string; total: number; color: string }) => item.label),
        counts.map((item: { label: string; total: number; color: string }) => item.color)
      );
    },
    resultTeams(): any[] {
      return ((this.kpiResultsByTeam as any[]) || []).filter(
        (team: any) => (Number(team.totalResults) || 0) > 0
      );
    },
    hasResultTeamData(): boolean {
      return this.resultTeams.length > 0;
    },
    resultTeamKey(): string {
      return this.resultTeams.map((team: any) => team.teamName).join("-");
    },
    resultTeamSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(this.resultTeams, KPI_TEAM_RESULT_ORDER);
    },
    resultTeamChartOptions(): Record<string, any> {
      const teams: any[] = this.resultTeams;
      return stackedBarOptions(
        teams.map((team: any) => team.teamName),
        this.resultTeamSeries.map((series: { name: string; data: number[] }) => series.name)
      );
    },
    resultLocations(): any[] {
      return ((this.kpiResultsByLocation as any[]) || []).filter(
        (location: any) => (Number(location.totalResults) || 0) > 0
      );
    },
    hasResultLocationData(): boolean {
      return this.resultLocations.length > 0;
    },
    resultLocationKey(): string {
      return this.resultLocations.map((location: any) => location.locationName).join("-");
    },
    resultLocationSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(this.resultLocations, KPI_RESULT_ORDER);
    },
    resultLocationChartOptions(): Record<string, any> {
      return stackedBarOptions(
        this.resultLocations.map((location: any) => location.locationName),
        this.resultLocationSeries.map((series: { name: string; data: number[] }) => series.name)
      );
    },
  },
});
</script>
